import React, { useState, useEffect } from 'react';
import { Users, Eye, TrendingUp, Activity, Globe, RefreshCw } from 'lucide-react';
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '';

const AdminAnalytics = () => {
  const [analytics, setAnalytics] = useState({
    totalVisitors: 0,
    totalPageViews: 0,
    todayVisitors: 0,
    todayPageViews: 0,
    popularPages: [],
    dailyVisits: [],
    recentVisitors: [],
    referrers: []
  });
  const [activeUsers, setActiveUsers] = useState({ activeUsers: 0, currentPages: [] });
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('week');
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    fetchAnalytics();
    fetchActiveUsers();
    const interval = setInterval(fetchActiveUsers, 30000);
    return () => clearInterval(interval);
  }, [period]);

  const fetchAnalytics = async () => {
    const token = localStorage.getItem('adminToken');
    try {
      const response = await axios.get(`${API_BASE_URL}/api/admin/analytics?period=${period}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAnalytics(response.data);
    } catch (error) {
      console.error('Error fetching analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchActiveUsers = async () => {
    const token = localStorage.getItem('adminToken');
    try {
      const response = await axios.get(`${API_BASE_URL}/api/admin/analytics/active`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setActiveUsers(response.data);
    } catch (error) {
      console.error('Error fetching active users:', error);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await Promise.all([fetchAnalytics(), fetchActiveUsers()]);
    setRefreshing(false);
  };

  const statCards = [
    { title: 'Total Visitors', value: analytics.totalVisitors, icon: Users, gradient: 'from-blue-500 to-blue-600' },
    { title: 'Total Page Views', value: analytics.totalPageViews, icon: Eye, gradient: 'from-green-500 to-green-600' },
    { title: "Today's Visitors", value: analytics.todayVisitors, icon: Activity, gradient: 'from-purple-500 to-purple-600' },
    { title: "Today's Views", value: analytics.todayPageViews, icon: TrendingUp, gradient: 'from-orange-500 to-orange-600' },
    { title: 'Active Now', value: activeUsers.activeUsers, icon: Globe, gradient: 'from-red-500 to-red-600' },
  ];

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="w-10 h-10 border-3 border-orange-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-white">Website Analytics</h1>
        <div className="flex space-x-3">
          <select
            value={period}
            onChange={(e) => setPeriod(e.target.value)}
            className="px-3 py-2 bg-dark-700 border border-dark-600 rounded-lg focus:outline-none focus:border-orange-500 text-white"
          >
            <option value="today">Today</option>
            <option value="week">Last 7 Days</option>
            <option value="month">Last 30 Days</option>
          </select>
          <button
            onClick={handleRefresh}
            disabled={refreshing}
            className="bg-orange-500 text-white px-4 py-2 rounded-lg hover:bg-orange-600 transition flex items-center space-x-2"
          >
            <RefreshCw size={18} className={refreshing ? 'animate-spin' : ''} />
            <span>Refresh</span>
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 mb-8">
        {statCards.map((stat, index) => (
          <div key={index} className="bg-dark-900 rounded-lg p-4 border border-dark-700">
            <div className="flex items-center justify-between mb-2">
              <div className={`bg-gradient-to-r ${stat.gradient} p-2 rounded-lg`}>
                <stat.icon size={20} className="text-white" />
              </div>
              <span className="text-2xl font-bold text-white">{stat.value}</span>
            </div>
            <p className="text-gray-400 text-sm">{stat.title}</p>
          </div>
        ))}
      </div>

      <div className="grid md:grid-cols-2 gap-6 mb-6">
        <div className="bg-dark-900 rounded-lg border border-dark-700">
          <div className="p-4 border-b border-dark-700">
            <h2 className="text-lg font-bold text-white">Most Popular Pages</h2>
          </div>
          <div className="divide-y divide-dark-700">
            {analytics.popularPages.map((page, index) => (
              <div key={index} className="p-4 flex justify-between items-center">
                <span className="text-gray-300 text-sm">{page.page_url || 'Home'}</span>
                <span className="font-semibold text-orange-500">{page.view_count} views</span>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-dark-900 rounded-lg border border-dark-700">
          <div className="p-4 border-b border-dark-700">
            <h2 className="text-lg font-bold text-white">Traffic Sources</h2>
          </div>
          <div className="divide-y divide-dark-700">
            {analytics.referrers.map((source, index) => (
              <div key={index} className="p-4 flex justify-between items-center">
                <span className="text-gray-300 text-sm">{source.source}</span>
                <span className="font-semibold text-orange-500">{source.count} visits</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="bg-dark-900 rounded-lg border border-dark-700 mb-6">
        <div className="p-4 border-b border-dark-700">
          <h2 className="text-lg font-bold text-white">Daily Visitors</h2>
        </div>
        <div className="p-4">
          <div className="space-y-3">
            {analytics.dailyVisits.map((day, index) => (
              <div key={index}>
                <div className="flex justify-between text-sm mb-1">
                  <span className="text-gray-400">{new Date(day.visit_date).toLocaleDateString()}</span>
                  <span className="text-white">{day.unique_visitors} unique visitors</span>
                </div>
                <div className="w-full bg-dark-700 rounded-full h-2">
                  <div 
                    className="bg-orange-500 rounded-full h-2"
                    style={{ width: `${Math.min(100, (day.unique_visitors / Math.max(...analytics.dailyVisits.map(d => d.unique_visitors), 1)) * 100)}%` }}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="bg-dark-900 rounded-lg border border-dark-700">
        <div className="p-4 border-b border-dark-700">
          <h2 className="text-lg font-bold text-white">Recent Visitors</h2>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-dark-800">
              <tr>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-400">Time</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-400">Page</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-400">IP Address</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-dark-700">
              {analytics.recentVisitors.map((visitor, index) => (
                <tr key={index} className="hover:bg-dark-800">
                  <td className="px-4 py-3 text-sm text-gray-400">
                    {new Date(visitor.visited_at).toLocaleString()}
                  </td>
                  <td className="px-4 py-3 text-sm text-white">{visitor.page_url || '/'}</td>
                  <td className="px-4 py-3 text-sm text-gray-400">{visitor.ip_address || 'N/A'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default AdminAnalytics;